import { mat4, vec3, ReadonlyMat4 } from 'gl-matrix';
import { Grid } from '~/engine/data/common/Grid';
import { patientPositionFromOrientation } from '~/utils/orientation';

/**
 * Check whether a grid's orientation is identity HFS (Head-First Supine).
 * Returns true only when the 3x3 rotation block equals identity
 * within the given tolerance.
 *
 * Axis-aligned non-HFS orientations (e.g. FFS with [1,0,0, 0,-1,0])
 * return false.
 */
export function isHfsAligned(grid: Grid, tolerance = 1e-4): boolean {
    return (
        patientPositionFromOrientation(grid.getOrientation(), tolerance) ===
        'HFS'
    );
}

export interface ResampleResult<T extends Int16Array | Uint8Array> {
    pixels: T;
    dstGrid: Grid;
    /**
     * QA matrix: maps derived voxel indices [i',j',k',1] -> original voxel indices [i,j,k,1]
     * v_src = qaVoxelDstToSrc * v_dst
     */
    qaVoxelDstToSrc: mat4;
}

export interface ArbitraryResampleOptions {
    /**
     * Matrix in *patient space* mapping destination world coords to source world coords:
     *
     *   p_src = patientSrcFromDst * p_dst
     *
     * If omitted, identity is used (dst patient == src patient).
     */
    patientSrcFromDst?: ReadonlyMat4;

    /**
     * Value to use outside source volume bounds.
     * For Int16 CT/MR this is often 0; for Uint8 SDF/masks maybe 0 or 255.
     */
    backgroundValue?: number;

    /**
     * Whether to use fast 8-voxel average instead of full trilinear.
     * (Keep false if you care about exact interpolation, e.g. SDF.)
     */
    fastAverageInsteadOfTrilinear?: boolean;

    /**
     * If true, clamp out-of-bounds sample positions to the
     * nearest edge voxel instead of returning backgroundValue.
     * Produces a natural edge-extension effect.
     */
    clampToEdge?: boolean;

    /** Optional clamp range; if omitted, derived from output typed array type. */
    clampMin?: number;
    clampMax?: number;
}

/**
 * Build voxelDst -> voxelSrc mapping for arbitrary src/dst grids
 * (and optional patient-space registration).
 */
export function buildQaVoxelDstToSrc(
    srcGrid: Grid,
    dstGrid: Grid,
    patientSrcFromDst?: ReadonlyMat4
): mat4 {
    // src: voxel -> world
    const T_src_phys = srcGrid.voxelToWorld as mat4;
    // dst: voxel -> world
    const T_dst_phys = dstGrid.voxelToWorld as mat4;

    const invSrc = mat4.create();
    if (!mat4.invert(invSrc, T_src_phys)) {
        throw new Error('Source voxelToWorld transform is not invertible.');
    }

    // patientSrcFromDst is world_dst -> world_src
    const M_patientSrc_from_patientDst = patientSrcFromDst
        ? (patientSrcFromDst as mat4)
        : mat4.create();

    if (!patientSrcFromDst) {
        mat4.identity(M_patientSrc_from_patientDst);
    }

    // qaVoxelDstToSrc = inv(T_src_phys) * M_patientSrc_from_patientDst * T_dst_phys
    const qaVoxelDstToSrc = mat4.create();
    const tmp = mat4.create();
    mat4.multiply(tmp, M_patientSrc_from_patientDst, T_dst_phys);
    mat4.multiply(qaVoxelDstToSrc, invSrc, tmp);

    return qaVoxelDstToSrc;
}
export function resampleGridCPU<
    TIn extends Int16Array | Uint8Array,
    TOut extends Int16Array | Uint8Array,
>(
    inputPixels: TIn,
    srcGrid: Grid,
    dstGrid: Grid,
    outputCtor: { new (length: number): TOut },
    options: ArbitraryResampleOptions = {}
): ResampleResult<TOut> {
    const srcNx = srcGrid.sizeX | 0;
    const srcNy = srcGrid.sizeY | 0;
    const srcNz = srcGrid.sizeZ | 0;

    if (inputPixels.length !== srcNx * srcNy * srcNz) {
        throw new Error('Input pixel data length does not match srcGrid size.');
    }

    const dstNx = dstGrid.sizeX | 0;
    const dstNy = dstGrid.sizeY | 0;
    const dstNz = dstGrid.sizeZ | 0;

    // Build voxelDst -> voxelSrc mapping
    const qaVoxelDstToSrc = buildQaVoxelDstToSrc(
        srcGrid,
        dstGrid,
        options.patientSrcFromDst
    );
    const M = qaVoxelDstToSrc;

    // Decompose into “origin + 3 steps” form for efficient iteration
    const c0 = vec3.fromValues(M[0], M[1], M[2]); // step when i++
    const c1 = vec3.fromValues(M[4], M[5], M[6]); // step when j++
    const c2 = vec3.fromValues(M[8], M[9], M[10]); // step when k++
    const c3 = vec3.fromValues(M[12], M[13], M[14]); // src voxel for [0,0,0,1]

    const baseZ = vec3.create();
    const rowStart = vec3.create();
    const srcPos = vec3.create();

    const outPixels = new outputCtor(dstNx * dstNy * dstNz);

    const backgroundValue = options.backgroundValue ?? 0;

    // Determine clamp range from options or typed array type
    let clampMin: number;
    let clampMax: number;

    if (options.clampMin !== undefined && options.clampMax !== undefined) {
        clampMin = options.clampMin;
        clampMax = options.clampMax;
    } else if (outPixels instanceof Int16Array) {
        clampMin = -32768;
        clampMax = 32767;
    } else {
        // Uint8
        clampMin = 0;
        clampMax = 255;
    }

    const fastAverage = !!options.fastAverageInsteadOfTrilinear;
    const clampToEdge = !!options.clampToEdge;

    const sampleTrilinear = (x: number, y: number, z: number): number => {
        let x0 = Math.floor(x);
        let y0 = Math.floor(y);
        let z0 = Math.floor(z);
        const x1 = x0 + 1;
        const y1 = y0 + 1;
        const z1 = z0 + 1;

        // completely outside source volume
        if (
            x1 < 0 ||
            y1 < 0 ||
            z1 < 0 ||
            x0 >= srcNx ||
            y0 >= srcNy ||
            z0 >= srcNz
        ) {
            if (!clampToEdge) {
                return backgroundValue;
            }
            // Edge-extend: clamp to nearest edge voxel
            const ci = Math.max(0, Math.min(srcNx - 1, Math.round(x)));
            const cj = Math.max(0, Math.min(srcNy - 1, Math.round(y)));
            const ck = Math.max(0, Math.min(srcNz - 1, Math.round(z)));
            return inputPixels[ci + cj * srcNx + ck * srcNx * srcNy];
        }

        // clamp indices
        if (x0 < 0) {
            x0 = 0;
        }
        if (y0 < 0) {
            y0 = 0;
        }
        if (z0 < 0) {
            z0 = 0;
        }
        const x0c = x0;
        const y0c = y0;
        const z0c = z0;
        const x1c = x1 >= srcNx ? srcNx - 1 : x1;
        const y1c = y1 >= srcNy ? srcNy - 1 : y1;
        const z1c = z1 >= srcNz ? srcNz - 1 : z1;

        const idx000 = x0c + y0c * srcNx + z0c * srcNx * srcNy;
        const idx100 = x1c + y0c * srcNx + z0c * srcNx * srcNy;
        const idx010 = x0c + y1c * srcNx + z0c * srcNx * srcNy;
        const idx110 = x1c + y1c * srcNx + z0c * srcNx * srcNy;
        const idx001 = x0c + y0c * srcNx + z1c * srcNx * srcNy;
        const idx101 = x1c + y0c * srcNx + z1c * srcNx * srcNy;
        const idx011 = x0c + y1c * srcNx + z1c * srcNx * srcNy;
        const idx111 = x1c + y1c * srcNx + z1c * srcNx * srcNy;

        const a = inputPixels[idx000];
        const b = inputPixels[idx100];
        const c = inputPixels[idx010];
        const d = inputPixels[idx110];
        const e = inputPixels[idx001];
        const f = inputPixels[idx101];
        const g = inputPixels[idx011];
        const h = inputPixels[idx111];

        if (fastAverage) {
            // super-cheap approximation
            return (a + b + c + d + e + f + g + h) / 8.0;
        }

        const tx = x - x0;
        const ty = y - y0;
        const tz = z - z0;

        const c00 = a * (1 - tx) + b * tx;
        const c10 = c * (1 - tx) + d * tx;
        const c01 = e * (1 - tx) + f * tx;
        const c11 = g * (1 - tx) + h * tx;

        const c0v = c00 * (1 - ty) + c10 * ty;
        const c1v = c01 * (1 - ty) + c11 * ty;

        return c0v * (1 - tz) + c1v * tz;
    };

    let outIndex = 0;

    for (let k = 0; k < dstNz; k++) {
        // baseZ = k * c2 + c3
        vec3.scale(baseZ, c2, k);
        vec3.add(baseZ, baseZ, c3);

        for (let j = 0; j < dstNy; j++) {
            // rowStart = baseZ + j * c1
            vec3.scale(rowStart, c1, j);
            vec3.add(rowStart, rowStart, baseZ);

            srcPos[0] = rowStart[0];
            srcPos[1] = rowStart[1];
            srcPos[2] = rowStart[2];

            for (let i = 0; i < dstNx; i++, outIndex++) {
                const val = sampleTrilinear(srcPos[0], srcPos[1], srcPos[2]);

                // clamp & round to integral voxel
                let v = Math.round(val);
                if (v < clampMin) {
                    v = clampMin;
                }
                if (v > clampMax) {
                    v = clampMax;
                }

                outPixels[outIndex] = v;

                // step one voxel in i
                srcPos[0] += c0[0];
                srcPos[1] += c0[1];
                srcPos[2] += c0[2];
            }
        }
    }

    return {
        pixels: outPixels,
        dstGrid,
        qaVoxelDstToSrc,
    };
}

export function resampleInt16Grid(
    srcPixels: Int16Array,
    srcGrid: Grid,
    dstGrid: Grid,
    options: ArbitraryResampleOptions = {}
): ResampleResult<Int16Array> {
    return resampleGridCPU(srcPixels, srcGrid, dstGrid, Int16Array, {
        backgroundValue: options.backgroundValue ?? 0,
        ...options,
        clampMin: options.clampMin ?? -32768,
        clampMax: options.clampMax ?? 32767,
    });
}

export function resampleUint8Grid(
    srcPixels: Uint8Array,
    srcGrid: Grid,
    dstGrid: Grid,
    options: ArbitraryResampleOptions = {}
): ResampleResult<Uint8Array> {
    return resampleGridCPU(srcPixels, srcGrid, dstGrid, Uint8Array, {
        backgroundValue: options.backgroundValue ?? 0,
        ...options,
        clampMin: options.clampMin ?? 0,
        clampMax: options.clampMax ?? 255,
    });
}

/**
 * Build a neutral, axis-aligned HFS grid that tightly (or minimally)
 * encloses an arbitrary oblique source grid.
 *
 * This is the same geometry that your CPU/WebGL resamplers expect.
 *
 * @param srcGrid           Source grid (arbitrary orientation)
 * @param outNx             Desired output X voxel count
 * @param outNy             Desired output Y voxel count
 * @param outDz             Desired slice spacing (mm). If <= 0 → use srcGrid.resZ
 * @param inPlaneFovMm   Optional in-plane FOV override (mm).
 *                       When set, the output grid covers exactly
 *                       this extent (may crop or expand the source).
 *                       When omitted, the source AABB is used.
 */

export function buildNeutralHFSGrid(
    srcGrid: Grid,
    outNx: number,
    outNy: number,
    outDz: number,
    inPlaneFovMm?: number,
    zRangeMm?: [number, number]
): Grid {
    const srcNx = srcGrid.sizeX;
    const srcNy = srcGrid.sizeY;
    const srcNz = srcGrid.sizeZ;

    const T_src = srcGrid.voxelToWorld as mat4;

    // --- 1) World-space AABB of the source volume --------------------------
    const corners: vec3[] = [];
    const voxel = vec3.create();
    const world = vec3.create();

    const maxX = srcNx;
    const maxY = srcNy;
    const maxZ = srcNz;

    for (let ix = 0; ix <= 1; ix++) {
        for (let iy = 0; iy <= 1; iy++) {
            for (let iz = 0; iz <= 1; iz++) {
                voxel[0] = ix ? maxX : 0;
                voxel[1] = iy ? maxY : 0;
                voxel[2] = iz ? maxZ : 0;
                vec3.transformMat4(world, voxel, T_src);
                corners.push(vec3.clone(world));
            }
        }
    }

    const worldMin = vec3.fromValues(+Infinity, +Infinity, +Infinity);
    const worldMax = vec3.fromValues(-Infinity, -Infinity, -Infinity);
    for (const c of corners) {
        worldMin[0] = Math.min(worldMin[0], c[0]);
        worldMin[1] = Math.min(worldMin[1], c[1]);
        worldMin[2] = Math.min(worldMin[2], c[2]);

        worldMax[0] = Math.max(worldMax[0], c[0]);
        worldMax[1] = Math.max(worldMax[1], c[1]);
        worldMax[2] = Math.max(worldMax[2], c[2]);
    }

    // --- 1b) Safety padding (≈1 voxel)
    // For HFS-aligned sources the AABB exactly matches the grid
    // extent, so no padding is needed.  Oblique sources need padding
    // because axis-aligned bounding box corners may not cover the
    // rotated volume edges precisely.
    const srcIsHfs = isHfsAligned(srcGrid);
    const pad = srcIsHfs
        ? 0
        : Math.max(srcGrid.resX, srcGrid.resY, srcGrid.resZ) * 1.0;
    worldMin[0] -= pad;
    worldMin[1] -= pad;
    worldMin[2] -= pad;
    worldMax[0] += pad;
    worldMax[1] += pad;
    worldMax[2] += pad;

    // --- 1c) Optional Z range override
    if (zRangeMm !== undefined) {
        worldMin[2] = zRangeMm[0] - pad;
        worldMax[2] = zRangeMm[1] + pad;
    }

    // Extents along patient axes
    let extentX = worldMax[0] - worldMin[0];
    let extentY = worldMax[1] - worldMin[1];
    const extentZ = worldMax[2] - worldMin[2];

    // --- 2) In-plane FOV override ------------------------------------------
    if (inPlaneFovMm !== undefined && inPlaneFovMm > 0) {
        extentX = inPlaneFovMm;
        extentY = inPlaneFovMm;
    }

    const safeNx = outNx;
    const safeNy = outNy;

    // For HFS sources the AABB extent equals exactly N * res
    // (edge-to-edge), so divide by N.  For oblique sources the
    // AABB is larger than the rotated volume and dividing by (N-1)
    // provides necessary coverage margin.
    const minDx = extentX / (srcIsHfs ? safeNx : safeNx - 1);
    const minDy = extentY / (srcIsHfs ? safeNy : safeNy - 1);

    // isotropic in-plane
    const dxdy = Math.max(minDx, minDy);
    const dx = dxdy;
    const dy = dxdy;

    const dz = outDz > 0 ? outDz : srcGrid.resZ;

    // Choose outNz large enough to cover full Z extent
    const outNz = Math.max(1, Math.ceil(extentZ / dz));

    // Actual FOV with these spacings
    const fovX = safeNx * dx;
    const fovY = safeNy * dy;
    const fovZ = outNz * dz;

    // Center of padded AABB
    const center = vec3.create();
    vec3.add(center, worldMin, worldMax);
    vec3.scale(center, center, 0.5);

    // World position of voxel [0,0,0]
    const origin = vec3.fromValues(
        center[0] - 0.5 * fovX,
        center[1] - 0.5 * fovY,
        center[2] - 0.5 * fovZ
    );

    // --- 3) HFS transform (identity rotation, translated origin) -----------
    const T_rotTrans = mat4.create();
    mat4.identity(T_rotTrans);
    T_rotTrans[12] = origin[0];
    T_rotTrans[13] = origin[1];
    T_rotTrans[14] = origin[2];

    const dstGrid = new Grid(safeNx, safeNy, outNz, dx, dy, dz, T_rotTrans);

    return dstGrid;
}
